import { PageRef } from 'providers/page-ref';
import { FoodHeader, NutrientTargets } from '@cf-shared/data-model/food';
import { Ingredient } from '@cf-shared/data-model/ingredient';
import { Log_Meal } from '@cf-shared/data-model/log-meal';
import { getEntryLocalTimeInDesiredTimeZone } from '@cf-shared/data-model/user-log';
import { DateTime } from 'luxon';


export interface MealPhotoScannerMatchedFood {
  aiName: string;
  estimatedGrams: number;
  confidence: number;
  food: FoodHeader;        // matched DB food
  /** set when the user ticked "Weighed with peel" on the portion screen (#5544); estimatedGrams is then the eaten weight */
  weighedWithRefuse?: { percent: number };
}

export type MealPhotoScannerPageParams = {
  mode?: 'add' | 'replace';  // 'add' = append to meal, 'replace' = replace existing ingredients
  returnCallback?: string;
  local?: {
    returnContext?: unknown;
    /**
     * The day this scan's meal belongs to, as an ISO date, and the meal that will hold
     * the scanned foods. The scanner does not read them: it forwards them to the portion
     * screen it opens when a row is tapped, which is what lets that screen's macro card
     * offer its `Day` scope. `EditIngredientPageParams.local` holds the canonical account
     * of the three, including the **all three or none** rule.
     *
     * Absent when the caller does not know a meal and a day, in which case the portion
     * screen shows one scope position fewer, as it does on any other dayless path.
     */
    dayDate?: string;
    mealId?: string;
    mealName?: string;
    /**
     * What that meal holds right now, so the portion screen can total the meal AS IT WILL
     * BE with the scanned foods in it.
     *
     * Only counted in `add` mode. A `replace` scan throws the meal's ingredients away
     * before adding the scanned ones, so counting them would promise a day the save is
     * about to make untrue -- and that decision is made HERE, from this page's own mode,
     * not by the caller. See `mealIngredientsBesidesTheAdjustedRow`.
     */
    mealIngredients?: Ingredient[];
    /**
     * The meal's own nutrient targets, forwarded so the portion screen can offer its meal
     * scope as well as its day one.
     *
     * `Log_Meal.targets`, and never the stored `Log_Meal.planningTargets` — both callers
     * passed the latter until #5297, which graded a meal that already had food against a
     * target its own contents had made smaller. The rule and the measured cost are on
     * `EditIngredientPageParams.local.targets`, which is where this value ends up.
     *
     * Sent for consistency rather than for its own sake. Before the day was threaded, the
     * portion screen opened from here showed no scope dial at all, because neither wider
     * scope had anything to measure against; offering `Day` while the meal position stayed
     * missing would have been a stranger thing to look at than either. The running total
     * the meal scope needs is the same list `mealIngredients` and the scanned rows already
     * produce, so this costs one value and no new arithmetic.
     */
    mealTargets?: NutrientTargets;
  };
};

/** The values a scanner caller forwards about the meal it is scanning into. */
export type MealPhotoScanMealParams =
  Required<Pick<NonNullable<MealPhotoScannerPageParams['local']>, 'dayDate' | 'mealId' | 'mealName' | 'mealIngredients' | 'mealTargets'>>;

/**
 * Builds everything the scanner forwards to its portion screen about the meal, in one place.
 *
 * Every caller that opens the scanner for a meal goes through this, so a value the portion
 * screen needs is added once rather than once per caller. That matters because only the day
 * and the meal id are guarded on the scanner's side: a caller that forgot the ingredients
 * would still get an ordinary-looking day bar, drawn from a total that is quietly too low.
 *
 * @param ingredients what the meal holds right now. Taken from the caller rather than read
 *   off `meal` here, so a caller that repairs a missing list on read keeps doing so.
 * @param now the current local time, used only for its time zone when placing the meal on
 *   a day — the same `getEntryLocalTimeInDesiredTimeZone` call the pages use to put a meal
 *   on its day, so the two cannot answer differently.
 */
export function mealPhotoScanMealParams(meal: Log_Meal, ingredients: Ingredient[], now: DateTime): MealPhotoScanMealParams {
  return {
    dayDate: getEntryLocalTimeInDesiredTimeZone(meal, now).toISODate(),
    mealId: meal._id,
    mealName: meal.name,
    mealIngredients: ingredients,
    // the meal's OWN plan targets, never the stored `planningTargets`. that field is computed
    // with this meal's food already taken out of the day, so a meal that has food in it would
    // be graded against a target its own contents shrank. see
    // `design-docs/meal-target-figures.md` (#5297).
    mealTargets: meal.targets,
  };
}

export type MealPhotoScannerResult = {
  matchedFoods: MealPhotoScannerMatchedFood[];
};

export const IMealPhotoScannerPage: PageRef<MealPhotoScannerPageParams, MealPhotoScannerResult> = {
  pageName: 'MealPhotoScannerPage',
} as PageRef<MealPhotoScannerPageParams, MealPhotoScannerResult>;
